/**
 * WarehouseScenarios.jsx — Phase 4 of the layout roadmap.
 *
 * Landing page: pick a warehouse → see all its scenarios. Click "Compare"
 * on a scenario to run the pick-path simulator against BOTH the live layout
 * and the scenario, then render a Current vs Proposed metrics table.
 *
 * Scope for MVP: list, create (clones live layout), rename, delete, compare.
 * Rearranging tiles inside a scenario is deferred — the compare table already
 * proves the plumbing, and the SVG editor can be repointed at a scenario in
 * a follow-up (that's just a `scenario_id` query param on the designer).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowsSplit, Plus, Trash, PencilSimple, ChartLineUp, ArrowUp, ArrowDown, Minus, ArrowLeft, ArrowRight, FilePdf,
} from '@phosphor-icons/react';
import {
  getWarehouses, listScenarios, createScenario, deleteScenario, updateScenario, compareScenario,
  exportScenarioPdf,
} from '../lib/api';

const fmt = (n, s = '') => (n == null ? '—' : `${Math.round(n).toLocaleString()}${s}`);
const fmtPct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`);

export default function WarehouseScenarios() {
  const [params, setParams] = useSearchParams();
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWh, setSelectedWh] = useState(params.get('wh') || '');
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [compareId, setCompareId] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareDays, setCompareDays] = useState(30);

  // Initial load — warehouses.
  useEffect(() => {
    (async () => {
      try {
        const r = await getWarehouses();
        const list = r.data || [];
        setWarehouses(list);
        if (!selectedWh) {
          const def = list.find((w) => w.is_default) || list[0];
          if (def) { setSelectedWh(def.id); setParams({ wh: def.id }); }
        }
      } catch (e) { toast.error('Failed to load warehouses'); }
    })();
  }, []);

  // Reload scenarios when warehouse changes.
  useEffect(() => {
    if (!selectedWh) return;
    (async () => {
      setLoading(true);
      try {
        const r = await listScenarios(selectedWh);
        setScenarios(r.data.scenarios || []);
      } catch (e) { toast.error('Failed to load scenarios'); }
      finally { setLoading(false); }
    })();
    setCompareId(null); setCompareData(null);
  }, [selectedWh]);

  const handleCreate = async () => {
    if (!newName.trim() || !selectedWh) return;
    setCreating(true);
    try {
      const r = await createScenario(selectedWh, { name: newName.trim() });
      setScenarios((prev) => [r.data, ...prev]);
      setCreateOpen(false); setNewName('');
      toast.success(`Scenario "${r.data.name}" created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete scenario "${s.name}"? This cannot be undone.`)) return;
    try {
      await deleteScenario(selectedWh, s.id);
      setScenarios((prev) => prev.filter((x) => x.id !== s.id));
      if (compareId === s.id) { setCompareId(null); setCompareData(null); }
      toast.success('Scenario deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const handleRename = async (s) => {
    const name = window.prompt('Rename scenario', s.name);
    if (!name || name === s.name) return;
    try {
      const r = await updateScenario(selectedWh, s.id, { name });
      setScenarios((prev) => prev.map((x) => x.id === s.id ? r.data : x));
    } catch (e) { toast.error('Rename failed'); }
  };

  const runCompare = async (s, days = compareDays) => {
    setCompareId(s.id); setCompareLoading(true); setCompareData(null);
    try {
      const r = await compareScenario(selectedWh, s.id, days);
      setCompareData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Compare failed');
    } finally { setCompareLoading(false); }
  };

  const [exportingPdf, setExportingPdf] = useState(false);
  const handleExportComparePdf = async () => {
    if (!selectedWh || !compareId) return;
    setExportingPdf(true);
    try {
      const r = await exportScenarioPdf(selectedWh, compareId, compareDays);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disp = r.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disp);
      a.href = url;
      a.download = (match && match[1]) || `scenario-compare-${compareId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Comparison PDF ready — attach it to your capex request.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  const activeScenario = useMemo(
    () => scenarios.find((s) => s.id === compareId) || null,
    [scenarios, compareId],
  );

  return (
    <Layout>
      <div className="max-w-7xl space-y-5" data-testid="warehouse-scenarios-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="overline">Warehouse</p>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ArrowsSplit size={22} weight="fill" className="text-brand" />
              What-If Scenarios
            </h1>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Clone your live layout, rearrange it hypothetically, and see how the pick-path simulator
              scores it against the current one. Great for justifying moves to leadership.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/warehouse-layout" className="text-xs text-brand hover:underline flex items-center gap-1">
              <ArrowLeft size={12} /> Back to designer
            </Link>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Warehouse</Label>
              <Select value={selectedWh} onValueChange={(v) => { setSelectedWh(v); setParams({ wh: v }); }}>
                <SelectTrigger className="w-56" data-testid="scenarios-wh-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setCreateOpen(true)} disabled={!selectedWh} data-testid="new-scenario-btn" className="bg-brand hover:bg-brand/90">
              <Plus size={12} className="mr-1" /> New scenario
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Scenario list */}
          <div className="col-span-4 space-y-2" data-testid="scenarios-list">
            {loading ? (
              <p className="text-xs text-muted-foreground">Loading scenarios…</p>
            ) : scenarios.length === 0 ? (
              <div className="border border-dashed border-border p-6 text-xs text-center text-muted-foreground">
                <ArrowsSplit size={24} className="mx-auto mb-2 text-muted-foreground/60" />
                No scenarios yet. Click <span className="font-bold">&ldquo;New scenario&rdquo;</span> to clone your current layout.
              </div>
            ) : (
              scenarios.map((s) => {
                const active = compareId === s.id;
                return (
                  <div
                    key={s.id}
                    className={`border p-3 space-y-1 ${active ? 'border-brand bg-brand/5' : 'border-border'}`}
                    data-testid={`scenario-card-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-sm truncate">{s.name}</p>
                      <Badge variant="outline" className="text-[10px]">{(s.tiles || []).length} tiles</Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Created {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                    </p>
                    <div className="flex items-center gap-1 pt-1">
                      <Button size="sm" variant={active ? 'default' : 'outline'} className={active ? 'bg-brand hover:bg-brand/90' : ''} onClick={() => runCompare(s)} data-testid={`compare-btn-${s.id}`}>
                        <ChartLineUp size={12} className="mr-1" /> Compare
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleRename(s)} data-testid={`rename-btn-${s.id}`}>
                        <PencilSimple size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => handleDelete(s)} data-testid={`delete-scenario-btn-${s.id}`}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Compare panel */}
          <div className="col-span-8" data-testid="compare-panel">
            {!compareId ? (
              <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground h-full flex flex-col items-center justify-center">
                <ChartLineUp size={28} className="mb-2 text-muted-foreground/60" />
                Pick a scenario on the left, then click <span className="font-bold mx-1">Compare</span> to run the simulator against both layouts.
              </div>
            ) : (
              <div className="border border-border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="overline">Comparing</p>
                    <h2 className="text-lg font-bold">{activeScenario?.name}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">History window</Label>
                    <Select value={String(compareDays)} onValueChange={(v) => { setCompareDays(Number(v)); if (activeScenario) runCompare(activeScenario, Number(v)); }}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid="compare-days-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleExportComparePdf}
                      disabled={!compareData || exportingPdf}
                      data-testid="export-compare-pdf-btn"
                      title="Download side-by-side comparison PDF for capex sign-off"
                    >
                      <FilePdf size={12} className="mr-1" />
                      {exportingPdf ? 'Preparing…' : 'Export PDF'}
                    </Button>
                  </div>
                </div>

                {compareLoading ? (
                  <p className="text-xs text-muted-foreground">Running simulator against both layouts…</p>
                ) : compareData ? (
                  <>
                    {(compareData.current_sim?.issues?.length > 0 || compareData.proposed_sim?.issues?.length > 0) && (
                      <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 p-2">
                        <strong>Heads up:</strong>{' '}
                        {compareData.current_sim?.issues?.includes('no_dispatch_tile') && 'Live layout is missing a Dispatch tile — anchor is required for path calculation. '}
                        {compareData.current_sim?.issues?.includes('no_linked_locations') && 'Live layout has no rack tiles linked to real locations yet. '}
                        {compareData.proposed_sim?.issues?.includes('no_dispatch_tile') && 'Scenario is missing a Dispatch tile. '}
                        {compareData.proposed_sim?.issues?.includes('no_linked_locations') && 'Scenario has no linked location tiles. '}
                        Once fixed, the metrics will populate.
                      </div>
                    )}
                    <div className="overflow-x-auto" data-testid="compare-table">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="text-left px-3 py-2">Metric</th>
                            <th className="text-right px-3 py-2">Current</th>
                            <th className="text-right px-3 py-2">Proposed</th>
                            <th className="text-right px-3 py-2">Δ Absolute</th>
                            <th className="text-right px-3 py-2">Δ %</th>
                          </tr>
                        </thead>
                        <tbody>
                          <CompareRow label="Total walking distance (m)" k="total_distance_m" metrics={compareData.metrics} lowerIsBetter />
                          <CompareRow label="Avg distance / ticket (m)" k="avg_distance_per_ticket_m" metrics={compareData.metrics} lowerIsBetter />
                          <CompareRow label="Total picks" k="total_picks" metrics={compareData.metrics} />
                          <CompareRow label="Avg picks / ticket" k="avg_picks_per_ticket" metrics={compareData.metrics} />
                          <CompareRow label="Tickets analysed" k="tickets_analyzed" metrics={compareData.metrics} />
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Simulator ran against {compareData.days} days of completed pick lists · nearest-neighbour walking distance in grid units (1 cell ≈ 1 m).
                    </p>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="new-scenario-dialog">
          <DialogHeader>
            <DialogTitle>New scenario</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-1">
              This clones your current live layout into a hypothetical version you can tweak without affecting production.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Move top-100 to Golden Zone"
              className="mt-1"
              autoFocus
              data-testid="scenario-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newName.trim() || creating} className="bg-brand hover:bg-brand/90" data-testid="create-scenario-btn">
              <Plus size={12} className="mr-1" /> {creating ? 'Creating…' : 'Create scenario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const CompareRow = ({ label, k, metrics, lowerIsBetter = false }) => {
  const m = metrics[k] || {};
  const abs = m.delta?.absolute ?? 0;
  const pct = m.delta?.pct;
  // Improvement if lowerIsBetter and delta<0, or !lowerIsBetter and delta>0.
  const good = lowerIsBetter ? abs < 0 : abs > 0;
  const flat = abs === 0;
  const Icon = flat ? Minus : (abs > 0 ? ArrowUp : ArrowDown);
  const colour = flat ? 'text-muted-foreground' : (good ? 'text-emerald-700' : 'text-red-700');
  return (
    <tr className="border-t border-border" data-testid={`compare-row-${k}`}>
      <td className="px-3 py-2 font-medium">{label}</td>
      <td className="px-3 py-2 text-right tabular-nums font-mono">{fmt(m.current)}</td>
      <td className="px-3 py-2 text-right tabular-nums font-mono font-bold">{fmt(m.proposed)}</td>
      <td className={`px-3 py-2 text-right tabular-nums font-mono ${colour}`}>
        <span className="inline-flex items-center gap-1 justify-end">
          <Icon size={11} />
          {fmt(abs)}
        </span>
      </td>
      <td className={`px-3 py-2 text-right tabular-nums font-mono ${colour}`}>{fmtPct(pct)}</td>
    </tr>
  );
};
